import { useDispatch, useSelector } from 'react-redux';
import { ApolloError, DocumentNode, gql, useQuery } from '@apollo/client';
import { RootState } from '../Store';
import { isEqual } from 'lodash';
import { styled } from 'styletron-react';
import { colors, glyphs } from '../Styles';
import { useEffect } from 'react';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';


// TODO CTO: consider Didn't consider that we are currently loading a multitude of things (e.g. both section and animals) to so it would be weird to wrap then wrap then wrap again
export const withApolloRedux = <P extends {}, T>(
  WrappedComponent: React.ComponentType<P>,
  query: DocumentNode,
  options?: {
    mapStoreToProps?: (state: RootState) => Partial<T>;
    mapStoreToTimestamp: (state: RootState) => Date;
    storeDispatch: ActionCreatorWithPayload<T>
    shouldRefetch?: (storeData: any) => boolean;
  }
) => {
  return function EnhancedComponent(props: P) {
    const dispatch = useDispatch();
    const storeData = useSelector((state: RootState) => 
      options?.mapStoreToProps ? options.mapStoreToProps(state) : null
    );

    const fetchedTimestamp = useSelector((state:RootState) => options?.mapStoreToTimestamp(state))

    const { loading, error, data} = useQuery(query, {
      skip: fetchedTimestamp ? (Date.now() - fetchedTimestamp.getTime()) > 300000 : false,
      fetchPolicy: 'cache-first',
      onCompleted: (result) => {
        if (!isEqual(result, storeData)) {
          dispatch({ type: 'UPDATE_DATA', payload: result });
        }
      }
    });
    
    useEffect(() => {
     if (!loading && !error && !isEqual(data, storeData) && options?.storeDispatch) {
        dispatch(options.storeDispatch(data))
     } 
    },[])

    if (loading) return <LoadingSpinner />;
    if (error) return <ErrorComponent error={error} />;

    return <WrappedComponent {...props} />;
  };
};

export function LoadingSpinner({color = colors.blueDarkPrimary, size}: {color?: colors, size?: "string"}) {
  return(
    <div style={{display:"flex", justifyContent: "center", textAlign: "center", color}}>
      {process.env.REACT_APP_PIG_SPINNER ? <LoadingPig size={size}/> :  <Spinner/>}
    </div>
  )
}

function ErrorComponent(props: {error: ApolloError}) {
  return <div>{props.error.message}</div>
}

export function LoadingPig({color = "#FABFB7", size = "120px"}: {size?: string, color?: colors | "#FABFB7"}){
  return <SpinnerDiv style={{color}}><glyphs.Animal fillColor={color as colors} size={size}/></SpinnerDiv>
}

const SpinnerDiv = styled("div", {
  width: "fit-content",
  height: "fit-content",
  animationName: "spin",
  animationDuration: "2s",
  animationTimingFunction: "linear",
  animationDelay: "0s",
  animationIterationCount: "infinite",
  animationFillMode: "none",
  animationPlayState: "running",
  animationTimeline: undefined,
  transformOrigin: "50% 50%",
  display: "content",
  "@Keyframes spin": {
    "0%": {transform: "rotate(0deg)"},
    "100%": { transform: "rotate(360deg)"},
  }
});

export const Spinner = styled("div", {
  border: "16px solid #f3f3f3", /* Light grey */
  borderTop: `16px solid`, /* Blue */
  borderRadius: "50%",
  width: "120px",
  height: "120px",
  animationName: "spin",
  animationDuration: "2s",
  animationTimingFunction: "linear",
  animationIterationCount: "infinite",
  animationPlayState: "running",
  "@Keyframes spin": {
    "0%": {transform: "rotate(0deg)"},
    "100%": { transform: "rotate(360deg)"},
  }
});

/*
// example of use
const UserComponent = ({ userId }) => {
  const EnhancedUser = withApolloRedux(
    UserDisplay,
    USER_QUERY,
    {
      mapStoreToProps: (state) => state.user,
      shouldRefetch: (storeData) => 
        !storeData || storeData.lastUpdated < Date.now() - 300000 // 5 minutes
    }
  );

  return <EnhancedUser userId={userId} />;
};


*/
